// components/OrderLog.tsx
import React from "react";
import SectionHeader from "components/SectionPillPropsIcons";
import { displayPaymentPaidByName } from "components/orderSummary/orderSummaryPaymentDetails";

/* =========================================================
   Types & status maps
   ========================================================= */
export type OrderKind = "installation" | "removal";

const INSTALL_STATUS_MAP: Record<number, string> = {
  100: "Draft",
  200: "Finalized",
  300: "Scheduled",
  400: "In Progress",
  500: "Completed",
  600: "Canceled",
};

const REMOVAL_STATUS_MAP: Record<number, string> = {
  100: "Draft",
  200: "Finalized",
  300: "Scheduled",
  400: "In Progress",
  500: "Completed",
  600: "Closed",
  700: "Canceled",
  800: "Void",
};

export type LogEvent =
  | { kind: "created"; at: string | number | Date }
  | {
      kind: "payment";
      at: string | number | Date;
      amount: number;
      method: string; // "online" | "Net 30" | etc.
      status: "Paid" | "Pending" | "Partial Payment" | "Awaiting Payment" | "Overpaid";
      paidBy?: string;
      paymentId?: string; // Transaction ID
    }
  | {
      kind: "status";
      at: string | number | Date;
      from: string | number; // allow numeric codes
      to: string | number;
      statusName?: string;
      label?: string; // "Installation Status:" | "Removal Status:"
    }
  | {
      kind: "send";
      at: string | number | Date;
      sendType: "quote" | "invoice";
      by?: string;
    }
  | {
      kind: "po";
      at: string | number | Date;
      poNumber: string;
      by?: string;
    };

export type OrderLogProps = {
  title: string;
  events: LogEvent[];
  orderKind?: OrderKind; // "installation" | "removal"
  statusMapOverride?: Record<string | number, string>; // optional custom override
};

/* =========================================================
   Date utils — handle Mongo {$date} & {$numberLong}, ISO, epoch
   ========================================================= */
function unwrapMongoDate(val: any): unknown {
  if (val && typeof val === "object" && "$date" in val) {
    const d = (val as any)["$date"];
    if (typeof d === "string") return d;
    if (d && typeof d === "object" && "$numberLong" in d) {
      const ms = Number(d["$numberLong"]);
      if (Number.isFinite(ms) && ms > 0) return ms; // skip sentinel negatives/zero
      return null;
    }
  }
  return val;
}

function toDate(val: unknown): Date | null {
  const raw = unwrapMongoDate(val);
  if (raw === null || raw === undefined) return null;

  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;

  if (typeof raw === "number") {
    const ms = raw < 1e12 ? raw * 1000 : raw; // seconds → ms
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s) return null;

    if (/^\d+$/.test(s)) return toDate(parseInt(s, 10)); // epoch seconds string

    const base = s.includes("T") ? s : s.replace(" ", "T");
    const tries = [s, base, /Z|[+-]\d{2}:\d{2}$/.test(base) ? base : base + "Z"];
    for (const t of tries) {
      const d = new Date(t);
      if (!isNaN(d.getTime())) return d;
    }
  }
  return null;
}

const fmtDateTime = (val: unknown) => {
  const d = toDate(val);
  return d ? d.toLocaleString() : "";
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(n);

/* =========================================================
   THEME — customize all colors here
   ========================================================= */
type Tone = { dot: string; headerBg: string; headerText: string; pillBg: string; pillText: string };
type PartialTone = Partial<Tone>;

const THEME = {
  /** payment color by status */
  paymentByStatus: {
    Paid:    { dot: "#2563eb", headerBg: "#e0f2fe", headerText: "#075985", pillBg: "#e0f2fe", pillText: "#075985" } as Tone,
    Pending: { dot: "#2563eb", headerBg: "#e0f2fe", headerText: "#075985", pillBg: "#e0f2fe", pillText: "#075985" } as Tone,
    "Partial Payment":  { dot: "#2563eb", headerBg: "#e0f2fe", headerText: "#075985", pillBg: "#e0f2fe", pillText: "#075985" } as Tone,
    "Awaiting Payment": { dot: "#2563eb", headerBg: "#e0f2fe", headerText: "#075985", pillBg: "#e0f2fe", pillText: "#075985" } as Tone,
    "Overpaid": { dot: "#2563eb", headerBg: "#e0f2fe", headerText: "#075985", pillBg: "#e0f2fe", pillText: "#075985" } as Tone,
  },
  /** optional overrides by payment METHOD (normalized lower-case) */
  paymentMethodOverride: {
    "net 30":   { headerBg: "#e0f2fe", headerText: "#075985", dot: "#2563eb" } as PartialTone,
    online:     { headerBg: "#e0f2fe", headerText: "#075985", dot: "#2563eb" } as PartialTone,
    card:       { headerBg: "#e0f2fe", headerText: "#075985", dot: "#2563eb" } as PartialTone,
    cash:       { headerBg: "#e0f2fe", headerText: "#075985", dot: "#2563eb" } as PartialTone,
    "bank transfer": { headerBg: "#e0f2fe", headerText: "#075985", dot: "#2563eb" } as PartialTone,
    // add more methods here without touching JSX
  } as Record<string, PartialTone>,

  /** default status tone + per-code overrides for installation/removal */
  statusDefault: { dot: "#00bc7d", headerBg: "#ecfdf5", headerText: "#006045", pillBg: "#dbeafe", pillText: "#193cb8" } as Tone,
  statusByCode: {
    installation: {
      100: { headerBg: "#eef2ff", headerText: "#3730a3", dot: "#6366f1", pillBg: "#eef2ff", pillText: "#3730a3" },
      200: { headerBg: "#e0f2fe", headerText: "#075985", dot: "#2563eb", pillBg: "#e0f2fe", pillText: "#075985" },
      300: { headerBg: "#f0fdf4", headerText: "#065f46", dot: "#10b981", pillBg: "#dbeafe", pillText: "#1e40af" },
      400: { headerBg: "#fff7ed", headerText: "#9a3412", dot: "#f59e0b", pillBg: "#fff7ed", pillText: "#9a3412" },
      500: { headerBg: "#ecfdf5", headerText: "#065f46", dot: "#10b981", pillBg: "#ecfdf5", pillText: "#065f46" },
      600: { headerBg: "#fef2f2", headerText: "#991b1b", dot: "#ef4444", pillBg: "#fee2e2", pillText: "#991b1b" },
    } as Record<number, PartialTone>,
    removal: {
      100: { headerBg: "#eef2ff", headerText: "#3730a3", dot: "#6366f1", pillBg: "#eef2ff", pillText: "#3730a3" },
      200: { headerBg: "#e0f2fe", headerText: "#075985", dot: "#2563eb", pillBg: "#e0f2fe", pillText: "#075985" },
      300: { headerBg: "#f0fdf4", headerText: "#065f46", dot: "#10b981", pillBg: "#dbeafe", pillText: "#1e40af" },
      400: { headerBg: "#fff7ed", headerText: "#9a3412", dot: "#f59e0b", pillBg: "#fff7ed", pillText: "#9a3412" },
      500: { headerBg: "#ecfdf5", headerText: "#065f46", dot: "#10b981", pillBg: "#ecfdf5", pillText: "#065f46" },
      600: { headerBg: "#f5f3ff", headerText: "#6d28d9", dot: "#a78bfa", pillBg: "#ede9fe", pillText: "#6d28d9" },
      700: { headerBg: "#fef2f2", headerText: "#991b1b", dot: "#ef4444", pillBg: "#fee2e2", pillText: "#991b1b" },
      800: { headerBg: "#faf5ff", headerText: "#6d28d9", dot: "#8b5cf6", pillBg: "#f3e8ff", pillText: "#6d28d9" },
    } as Record<number, PartialTone>,
  },
};

/* helpers to compose tones */
const mergeTone = (base: Tone, over?: PartialTone): Tone => ({ ...base, ...(over || {}) });
const methodKey = (s: string) => (s || "").toLowerCase().trim();

/* =========================================================
   Label mapping
   ========================================================= */
function labelFromCode(
  code: unknown,
  kind?: OrderKind,
  override?: Record<string | number, string>
): string {
  const tryNum =
    typeof code === "number"
      ? code
      : typeof code === "string" && /^\d+$/.test(code)
      ? parseInt(code, 10)
      : NaN;

  const map =
    override ||
    (kind === "installation"
      ? INSTALL_STATUS_MAP
      : kind === "removal"
      ? REMOVAL_STATUS_MAP
      : undefined);

  if (!isNaN(tryNum) && map && map[tryNum] != null) return map[tryNum];
  if (typeof code === "string" && code.trim()) return code;
  return String(code ?? "");
}

/* =========================================================
   UI atoms
   ========================================================= */
const Dot = ({ color }: { color: string }) => (
  <span
    className="d-inline-block rounded-circle"
    style={{ width: 10, height: 10, backgroundColor: color }}
  />
);

function HeaderRow({
  dotColor,
  bgColor,
  textColor,
  children,
}: {
  dotColor?: string;
  bgColor: string;
  textColor?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="px-3 py-2 d-flex align-items-center"
      style={{
        backgroundColor: bgColor,
        color: textColor,
        borderTopLeftRadius: ".75rem",
        borderTopRightRadius: ".75rem",
      }}
    >
      <div className="d-flex align-items-center gap-2">
        {dotColor && <Dot color={dotColor} />}
        <h5 className="fs-14 mb-0 p-2" style={{ color: textColor }}>{children}</h5>
      </div>
    </div>
  );
}

function Pill({ bg, fg, children }: { bg: string; fg: string; children: React.ReactNode }) {
  return (
    <span
      className="badge rounded-pill border p-2 fw-bold"
      style={{
        backgroundColor: bg,
        color: fg,
        borderColor: "rgba(0,0,0,.06)",
      }}
    >
      {children}
    </span>
  );
}

function EventCard({ header, children }: { header: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="border rounded-4 mb-3 overflow-hidden">
      {header}
      {children && <div className="px-3 py-3">{children}</div>}
    </div>
  );
}

/* =========================================================
   Main component
   ========================================================= */
export default function OrderLog({
  title,
  events,
  orderKind,
  statusMapOverride,
}: OrderLogProps) {
  // console.log("event :",events)
  return (
    <div className="card border-0 box-shadow shadow-border-3 rounded-4 no-scrollbar" style={{ height: 650, overflowY: "auto" }}>
      <div className="card-body">
        <div className="mb-3">
          <SectionHeader title={title} icon="ri-time-line" color="#4f39f6" size="md" divider />
        </div>

        {/* Created */}
        {events.filter(e => e.kind === "created").map((e, i) => {
          const when = fmtDateTime((e as any).at);
          return (
            <EventCard
              key={`created-${i}`}
              header={
                <HeaderRow bgColor="#f3f4f6" textColor="inherit">
                  Order Created on {when || "-"}
                </HeaderRow>
              }
            />
          );
        })}

        {/* Payments & Status */}
        {events.filter(e => e.kind !== "created").map((e, i) => {
          if (e.kind === "po") {
            const when = fmtDateTime(e.at);
            const tone = mergeTone(THEME.statusDefault, {
              headerBg: "#eef2ff",
              headerText: "#3730a3",
              dot: "#6366f1",
              pillBg: "#eef2ff",
              pillText: "#3730a3",
            });
            return (
              <EventCard
                key={`po-${i}`}
                header={
                  <HeaderRow dotColor={tone.dot} bgColor={tone.headerBg} textColor={tone.headerText}>
                    PO Number Updated
                  </HeaderRow>
                }
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div>PO Number:</div>
                  <Pill bg={tone.pillBg} fg={tone.pillText}>
                    {e.poNumber || "—"}
                  </Pill>
                </div>
                {e.by && (
                  <div className="d-flex align-items-center gap-2 text-muted mb-2">
                    <i className="ri-user-3-line" />
                    <div>
                      <span className="text-body">Updated by:</span> {e.by}
                    </div>
                  </div>
                )}
                {when && <div className="text-muted small">On {when}</div>}
              </EventCard>
            );
          }

          if (e.kind === "send") {
            const when = fmtDateTime(e.at);
            const tone = mergeTone(THEME.statusDefault, {
              headerBg: "#e0f2fe",
              headerText: "#075985",
              dot: "#2563eb",
              pillBg: "#e0f2fe",
              pillText: "#075985",
            });
            const label = e.sendType === "invoice" ? "Invoice" : "Quote";
            return (
              <EventCard
                key={`send-${i}`}
                header={
                  <HeaderRow dotColor={tone.dot} bgColor={tone.headerBg} textColor={tone.headerText}>
                    Status: {label}
                  </HeaderRow>
                }
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div>Status:</div>
                  <Pill bg={tone.pillBg} fg={tone.pillText}>
                    {label} Sent
                  </Pill>
                </div>
                {e.by && (
                  <div className="d-flex align-items-center gap-2 text-muted mb-2">
                    <i className="ri-user-3-line" />
                    <div>
                      <span className="text-body">By:</span> {e.by}
                    </div>
                  </div>
                )}
                {when && <div className="text-muted small">On {when}</div>}
              </EventCard>
            );
          }

          if (e.kind === "payment") {
            // base tone by status + optional method override
            const base = THEME.paymentByStatus[e.status];
            const tone = mergeTone(base, THEME.paymentMethodOverride[methodKey(e.method)]);
            const when = fmtDateTime(e.at);

            return (
              <EventCard
                key={`payment-${i}`}
                header={
                  <HeaderRow dotColor={tone.dot} bgColor={tone.headerBg} textColor={tone.headerText}>
                    Payment: {fmtMoney(e.amount)} via {e.method}
                  </HeaderRow>
                }
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="">Payment Status:</div>
                  <Pill bg={tone.pillBg} fg={tone.pillText}>{e.status}</Pill>
                </div>

                <div className="d-flex align-items-center gap-2 text-muted mb-2">
                    <i className="ri-user-3-line" />
                    <div>
                      <span className="text-body">Paid by:</span> {e.paidBy || "-"}
                    </div>
                  </div>

                {e.paymentId && (
                  <div className="d-flex align-items-center gap-2 text-muted mb-2">
                    <i className="ri-bank-card-line" />
                    <div>
                      <span className="text-body">Transaction ID:</span> {e.paymentId}
                    </div>
                  </div>
                )}

                {when && <div className="text-muted small">On {when}</div>}
              </EventCard>
            );
          }

          // STATUS rows: header tinted using code-aware theme; body shows left label + right pill.
          const toNum =
            typeof e.to === "number" ? e.to : /^\d+$/.test(String(e.to)) ? parseInt(String(e.to), 10) : NaN;
          const overrideByCode =
            orderKind && !isNaN(toNum)
              ? (THEME.statusByCode as any)[orderKind]?.[toNum]
              : undefined;

          const tone = mergeTone(THEME.statusDefault, overrideByCode);
          const when = fmtDateTime(e.at);
          const fromLabel = labelFromCode(e.from, orderKind, statusMapOverride);
          const toLabel   = labelFromCode(e.to,   orderKind, statusMapOverride);
          const statusText = e.statusName || `${fromLabel} → ${toLabel}`;

          return (
            <EventCard
              key={`status-${i}`}
              header={
                <HeaderRow dotColor={tone.dot} bgColor={tone.headerBg} textColor={tone.headerText}>
                  {e.label ||
                    (orderKind === "installation"
                      ? "Installation Status:"
                      : orderKind === "removal"
                      ? "Removal Status:"
                      : "Status:")}
                </HeaderRow>
              }
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="">Status:</div>
                <Pill bg={tone.pillBg} fg={tone.pillText}>
                  {statusText}
                </Pill>
              </div>
              {when && <div className="text-muted small">On {when}</div>}
            </EventCard>
          );
        })}
      </div>
    </div>
  );
}

/* =========================================================
   Public helpers — build events directly from your orders
   ========================================================= */
const normalizePayStatus = (s: any): "Paid" | "Pending" | "Partial Payment" | "Awaiting Payment" | "Overpaid" => {
  const v = String(s ?? "").toLowerCase();
  if (["paid", "success", "succeeded", "captured", "1", "true"].includes(v)) return "Paid";
  if (["pending", "0", "initiated", "processing"].includes(v)) return "Pending";
  if (["partial", "partial payment", "partial_payment"].includes(v)) return "Partial Payment";
  if (["awaiting", "awaiting payment", "awaiting_payment"].includes(v)) return "Awaiting Payment";
  if (["overpaid", "over_paid", "over_payment"].includes(v)) return "Overpaid";
  // Default fallback
  if (!v) return "Pending";
  return "Partial Payment";
};
const money = (n: any) => Number(n ?? 0) || 0;

export function buildInstallationLogEventsFromOrder(order: any): LogEvent[] {
  if (!order) return [];
  const evts: LogEvent[] = [];

  // Created
  evts.push({ kind: "created", at: order.created_at ?? order.createdAt });

  // Payments (installation uses payment_info[])
  (order.payment_info ?? []).forEach((p: any) => {
    evts.push({
      kind: "payment",
      at: p.payment_date,                               // {$date: "..."} shape
      amount: money(p.amount_paid ?? p.total_amount),   // show what actually paid; fallback total
      method: p.method || order.payment_method || "online",
      status: normalizePayStatus(p.current_payment_status || p.payment_status),
      paidBy: displayPaymentPaidByName(p),
      paymentId: p.payment_id || "", // Transaction ID
    });
  });

  // Status history
  (order.status_history ?? []).forEach((s: any) => {
    evts.push({
      kind: "status",
      at: s.timestamp,                                  // {$date: "..."}
      from: s.from,
      to: s.to,
      statusName: s.status_name || "",
      label: "Installation Status:",
    });
  });

  (order.send_quote_log ?? []).forEach((l: any) => {
    evts.push({
      kind: "send",
      at: l?.sent_at,
      sendType: "quote",
      by: l?.user_name || "",
    });
  });

  (order.send_invoice_log ?? []).forEach((l: any) => {
    evts.push({
      kind: "send",
      at: l?.sent_at,
      sendType: "invoice",
      by: l?.user_name || "",
    });
  });

  (order.po_number_log ?? []).forEach((l: any) => {
    evts.push({
      kind: "po",
      at: l?.sent_at,
      poNumber: String(l?.po_number_log ?? "").trim() || "—",
      by: l?.updated_by?.name || "",
    });
  });

  return evts;
}

export function buildRemovalLogEventsFromOrder(order: any): LogEvent[] {
  if (!order) return [];
  const evts: LogEvent[] = [];

  evts.push({ kind: "created", at: order.created_at ?? order.createdAt });

  (order.payment_info ?? []).forEach((p: any) => {
    evts.push({
      kind: "payment",
      at: p.payment_date,
      amount: money(p.amount_paid ?? p.total_amount),
      method: p.method || order.payment_method || "online",
      status: normalizePayStatus(p.current_payment_status || p.payment_status),
      paidBy: displayPaymentPaidByName(p),
      paymentId: p.payment_id || "", // Transaction ID
    });
  });

  (order.status_history ?? []).forEach((s: any) => {
    evts.push({
      kind: "status",
      at: s.timestamp,
      from: s.from,
      to: s.to,
      statusName: s.status_name || "",
      label: "Removal Status:",
    });
  });

  (order.send_quote_log ?? []).forEach((l: any) => {
    evts.push({
      kind: "send",
      at: l?.sent_at,
      sendType: "quote",
      by: l?.user_name || "",
    });
  });

  (order.send_invoice_log ?? []).forEach((l: any) => {
    evts.push({
      kind: "send",
      at: l?.sent_at,
      sendType: "invoice",
      by: l?.user_name || "",
    });
  });

  (order.po_number_log ?? []).forEach((l: any) => {
    evts.push({
      kind: "po",
      at: l?.sent_at,
      poNumber: String(l?.po_number_log ?? "").trim() || "—",
      by: l?.updated_by?.name || "",
    });
  });

  return evts;
}

export type BatchPaymentReportPaymentRef = {
  transaction_id?: string;
  batch_payment_id?: string;
  payment_id?: string;
  status?: string;
  amount?: number;
  payment_date?: string;
  method?: string;
  paid_by?: { name?: string };
};

function inferBatchPaymentMethod(ref: BatchPaymentReportPaymentRef): string {
  const method = String(ref.method || "").trim();
  if (method) return method;
  const paymentId = String(ref.payment_id || "").trim();
  if (paymentId.startsWith("pi_")) return "online";
  return "Batch Payment";
}

function batchPaymentRefStatus(
  ref: BatchPaymentReportPaymentRef,
  reportPaymentStatus?: string,
  reportRemainingBalance?: number
): "Paid" | "Pending" | "Partial Payment" | "Awaiting Payment" | "Overpaid" {
  const base = normalizePayStatus(ref.status);
  const reportPartial = String(reportPaymentStatus || "").toLowerCase() === "partial";
  if (base === "Paid" && reportPartial && Number(reportRemainingBalance || 0) > 0.0001) {
    return "Partial Payment";
  }
  return base;
}

/** Build OrderLog payment events from batch payment report `payment_refs`. */
export function buildBatchPaymentLogEventsFromReport(
  paymentRefs: BatchPaymentReportPaymentRef[] = [],
  options?: { reportPaymentStatus?: string; reportRemainingBalance?: number }
): LogEvent[] {
  const sorted = [...paymentRefs].sort((a, b) => {
    const ta = toDate(a.payment_date)?.getTime() ?? 0;
    const tb = toDate(b.payment_date)?.getTime() ?? 0;
    return tb - ta;
  });

  return sorted.map((ref) => ({
    kind: "payment" as const,
    at: ref.payment_date ?? "",
    amount: money(ref.amount),
    method: inferBatchPaymentMethod(ref),
    status: batchPaymentRefStatus(
      ref,
      options?.reportPaymentStatus,
      options?.reportRemainingBalance
    ),
    paidBy: String(ref.paid_by?.name || "").trim() || undefined,
    paymentId: String(ref.payment_id || ref.batch_payment_id || ref.transaction_id || "").trim() || undefined,
  }));
}
